import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getJobByPO } from '../utils/api';

/* On-Site Report — one simple page for the job site.
   Customer name, address and (optionally) phone; the scope of work (one line per product,
   add more if needed); how it's paid (cash and/or finance, and who financed it); and the
   total investment. Load a PO to fill it from the record, edit any line on screen, then
   Print / Save as PDF. Update Record → "On-Site Report" opens it filled in.            */

const n = v => { const x = parseFloat(String(v ?? '').replace(/[$,\s]/g, '')); return Number.isFinite(x) ? x : 0; };
const money = v => '$' + n(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).replace(/\.00$/, '');
const todayText = () => new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles', month: 'long', day: 'numeric', year: 'numeric' }).format(new Date());

// One scope line per distinct service picked on the record (Service 1–4)
function scopeFrom(job) {
  const seen = new Set();
  const rows = [];
  [job.svc1, job.svc2, job.svc3, job.svc4].forEach(s => {
    const name = String(s || '').trim();
    if (!name || seen.has(name.toLowerCase())) return;
    seen.add(name.toLowerCase());
    rows.push({ product: name, details: '' });
  });
  if (rows.length && String(job.scope || '').trim()) rows[0].details = String(job.scope).trim();
  return rows.length ? rows : [{ product: '', details: String(job.scope || '').trim() }];
}

// How it's paid: cash (any cash payment line) and/or finance (live loans, with the lender names)
function paymentFrom(job) {
  const cashLines = (job.cash_payments || []).filter(c => n(c.amount) && !['Waived', 'N/A'].includes(c.status));
  const loans = (job.lender_payments || []).filter(l => (l.lender_name || n(l.amount)) && !['No Go', 'Cancelled'].includes(l.status));
  const byLender = {};
  loans.forEach(l => { const k = String(l.lender_name || 'Lender').trim(); byLender[k] = (byLender[k] || 0) + n(l.amount); });
  return {
    cash: cashLines.length > 0,
    cashAmount: cashLines.reduce((s, c) => s + n(c.amount), 0),
    finance: loans.length > 0,
    lenders: Object.entries(byLender).map(([name, amount]) => ({ name, amount })),
  };
}

const CSS = `
.os-bar{display:flex;flex-wrap:wrap;gap:10px;align-items:center;margin-bottom:16px}
.os-bar .form-input{width:220px}
.os-btn{border:1px solid #0D3B2E;background:#0D3B2E;color:#fff;border-radius:10px;padding:9px 16px;font-weight:700;font-size:13px;cursor:pointer}
.os-btn.alt{background:#fff;color:#0D3B2E}
.os-btn:disabled{opacity:.5;cursor:default}
.os-msg{color:#B71C1C;font-size:13px;font-weight:600}
.os-toggle{display:flex;align-items:center;gap:8px;font-size:13px;font-weight:700;color:#0D3B2E;background:#EEF6F1;border:1px solid #B9D6C5;border-radius:10px;padding:8px 12px;cursor:pointer;user-select:none}
.os-toggle input{width:17px;height:17px;accent-color:#0D3B2E}
.os-stage{overflow-x:auto;padding:4px 0 20px}
.os-sheet{width:8.5in;min-height:11in;box-sizing:border-box;margin:0 auto;background:#fff;box-shadow:0 4px 22px rgba(0,0,0,.12);padding:.6in .65in;font-family:Arial,Helvetica,sans-serif;color:#1d2522;font-size:13px}
.os-top{display:flex;justify-content:space-between;align-items:flex-end;border-bottom:4px solid #0D3B2E;padding-bottom:10px}
.os-co{font-size:20px;font-weight:800;color:#0D3B2E;letter-spacing:.3px}
.os-co small{display:block;font-size:11px;font-weight:600;color:#6b7a73;letter-spacing:.5px;margin-top:2px}
.os-title{text-align:right}
.os-title b{display:block;font-size:24px;font-weight:900;letter-spacing:1.5px;color:#0D3B2E}
.os-title span{font-size:12px;color:#55625c}
.os-h{margin:22px 0 8px;font-size:12px;font-weight:900;letter-spacing:1.4px;color:#0D3B2E;text-transform:uppercase;border-bottom:1px solid #cfdcd5;padding-bottom:4px}
.os-grid{display:grid;grid-template-columns:130px 1fr;row-gap:8px;column-gap:12px;align-items:baseline}
.os-grid .k{font-weight:700;color:#55625c;font-size:12px;text-transform:uppercase;letter-spacing:.4px}
.os-grid .v{font-size:15px;font-weight:600;border-bottom:1px dotted #b8c5bf;min-height:20px;padding-bottom:2px}
.os-scope{width:100%;border-collapse:collapse}
.os-scope th{text-align:left;font-size:11px;letter-spacing:.5px;text-transform:uppercase;color:#55625c;border-bottom:2px solid #0D3B2E;padding:6px 6px}
.os-scope td{border-bottom:1px solid #dfe7e3;padding:5px 6px;vertical-align:top}
.os-scope td.num{width:26px;color:#8a9892;font-weight:700;padding-top:9px}
.os-scope td.prod{width:32%}
.os-in{width:100%;box-sizing:border-box;border:1px solid transparent;border-radius:6px;padding:4px 6px;font:inherit;font-size:14px;background:#F6FAF8;resize:vertical}
.os-in:focus{outline:none;border-color:#7FB79A;background:#fff}
.os-in.prod{font-weight:700}
.os-x{border:0;background:none;color:#c62828;font-weight:800;cursor:pointer;font-size:14px}
.os-add{margin-top:6px;border:1px dashed #7FB79A;background:#fff;color:#0D3B2E;border-radius:8px;padding:5px 12px;font-weight:700;font-size:12px;cursor:pointer}
.os-pay{display:flex;gap:28px;flex-wrap:wrap;font-size:15px;font-weight:700;margin-bottom:6px}
.os-pay span{display:inline-flex;align-items:center;gap:8px}
.os-box{display:inline-flex;width:17px;height:17px;border:2px solid #0D3B2E;border-radius:3px;align-items:center;justify-content:center;font-size:13px;font-weight:900;color:#0D3B2E}
.os-lenders{margin:6px 0 0;padding:0;list-style:none}
.os-lenders li{display:flex;justify-content:space-between;max-width:420px;border-bottom:1px dotted #b8c5bf;padding:4px 0;font-size:14px}
.os-total{margin-top:26px;display:flex;justify-content:space-between;align-items:center;background:#0D3B2E;color:#fff;border-radius:10px;padding:16px 20px}
.os-total b{font-size:14px;letter-spacing:1.5px;text-transform:uppercase}
.os-total span{font-size:28px;font-weight:900}
.os-sign{margin-top:40px;display:grid;grid-template-columns:1fr 1fr;gap:40px;font-size:11px;color:#55625c}
.os-sign div{border-top:1px solid #1d2522;padding-top:4px}
.os-empty{color:#8a9892;font-style:italic;font-size:13px}
@media print{
  @page{size:letter portrait;margin:0}
  body *{visibility:hidden!important}
  .os-sheet,.os-sheet *{visibility:visible!important}
  .os-stage{overflow:visible;padding:0}
  .os-sheet{position:absolute;left:0;top:0;box-shadow:none;margin:0;height:11in;min-height:0;overflow:hidden}
  .os-sheet,.os-sheet *{-webkit-print-color-adjust:exact;print-color-adjust:exact}
  .os-in{background:none!important;border:0!important;padding:2px 0;resize:none}
  .os-noprint{display:none!important}
  html,body{background:#fff!important}
}
`;

// Grows to fit its text, so long scope lines print in full
function AutoText({ value, onChange, className, placeholder }) {
  const rows = Math.max(1, String(value || '').split('\n').reduce((s, line) => s + Math.max(1, Math.ceil(line.length / 52)), 0));
  return <textarea className={className} rows={rows} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} />;
}

export default function OnSiteReport() {
  const [params, setParams] = useSearchParams();
  const [po, setPo] = useState(params.get('po') || '');
  const [job, setJob] = useState(null);
  const [scope, setScope] = useState([{ product: '', details: '' }]);
  const [showPhone, setShowPhone] = useState(true);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState('');

  async function load(p, thenPrint) {
    const q = String(p || '').trim();
    if (!q) return;
    setLoading(true); setMsg('');
    try {
      const j = await getJobByPO(q);
      setJob(j); setScope(scopeFrom(j)); setPo(j.po_number || q);
      setParams({ po: j.po_number || q }, { replace: true });
      if (thenPrint) setTimeout(() => window.print(), 400);
    } catch (e) {
      setJob(null);
      setMsg(e.response?.status === 404 ? `No record found for PO ${q}.` : 'Could not load that record.');
    } finally { setLoading(false); }
  }
  useEffect(() => { if (params.get('po')) load(params.get('po'), params.get('print') === '1'); }, []); // eslint-disable-line

  const j = job || {};
  const pay = job ? paymentFrom(job) : { cash: false, finance: false, lenders: [] };
  const total = n(j.change_order_amt) || n(j.contract_amt);   // Change Order Amount = the job's new total
  const address = [j.address, j.city].map(s => String(s || '').trim()).filter(Boolean).join(', ');
  const phones = [j.phone, j.phone_2].map(s => String(s || '').trim()).filter(Boolean).join('  ·  ');
  const names = [j.customer_name, j.customer_name_2].map(s => String(s || '').trim()).filter(Boolean).join(' & ');
  const setRow = (i, k, v) => setScope(rows => rows.map((r, x) => (x === i ? { ...r, [k]: v } : r)));

  return (
    <>
      <style>{CSS}</style>
      <div className="page-header">
        <h2>On-Site Report</h2>
        <div style={{ fontSize: 13, color: '#888' }}>Customer, scope of work, how it's paid and the total investment on one page. Load a PO to fill it in, edit any line, then print or save as PDF.</div>
      </div>
      <div className="page-body">
        <form className="os-bar" onSubmit={e => { e.preventDefault(); load(po); }}>
          <input className="form-input" placeholder="PO # (e.g. 26-1364-1)" value={po} onChange={e => setPo(e.target.value)} />
          <button className="os-btn alt" type="submit" disabled={loading || !po.trim()}>{loading ? 'Loading…' : 'Fill in from record'}</button>
          <label className="os-toggle">
            <input type="checkbox" checked={showPhone} onChange={e => setShowPhone(e.target.checked)} />
            Include phone number
          </label>
          <button className="os-btn" type="button" style={{ marginLeft: 'auto' }} onClick={() => window.print()}>🖨 Print / Save as PDF</button>
          {msg && <span className="os-msg">{msg}</span>}
        </form>

        <div className="os-stage">
          <div className="os-sheet">
            <div className="os-top">
              <div className="os-co">CPR Home Solutions Inc<small>{todayText()}</small></div>
              <div className="os-title"><b>ON-SITE REPORT</b><span>Job # {j.po_number || '______________'}</span></div>
            </div>

            <div className="os-h">Customer</div>
            <div className="os-grid">
              <div className="k">Name</div><div className="v">{names}</div>
              <div className="k">Address</div><div className="v">{address}</div>
              {showPhone && <><div className="k">Phone</div><div className="v">{phones}</div></>}
            </div>

            <div className="os-h">Scope of Work</div>
            <table className="os-scope">
              <thead><tr><th></th><th>Product</th><th>Details</th><th className="os-noprint"></th></tr></thead>
              <tbody>
                {scope.map((r, i) => (
                  <tr key={i}>
                    <td className="num">{i + 1}.</td>
                    <td className="prod"><AutoText className="os-in prod" value={r.product} placeholder="Product" onChange={v => setRow(i, 'product', v)} /></td>
                    <td><AutoText className="os-in" value={r.details} placeholder="What's being done (optional)" onChange={v => setRow(i, 'details', v)} /></td>
                    <td className="os-noprint" style={{ width: 24 }}>
                      {scope.length > 1 && <button type="button" className="os-x" title="Remove line" onClick={() => setScope(rows => rows.filter((_, x) => x !== i))}>✕</button>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" className="os-add os-noprint" onClick={() => setScope(rows => [...rows, { product: '', details: '' }])}>+ Add product</button>

            <div className="os-h">Payment</div>
            <div className="os-pay">
              <span><span className="os-box">{pay.cash ? '✓' : ''}</span>Cash</span>
              <span><span className="os-box">{pay.finance ? '✓' : ''}</span>Finance</span>
            </div>
            {pay.finance ? (
              <>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#55625c', textTransform: 'uppercase', letterSpacing: '.4px', marginTop: 8 }}>Financed by</div>
                <ul className="os-lenders">
                  {pay.lenders.map(l => <li key={l.name}><span>{l.name}</span><b>{n(l.amount) ? money(l.amount) : ''}</b></li>)}
                </ul>
              </>
            ) : job && !pay.cash ? <div className="os-empty">No payments entered on the record yet.</div> : null}

            <div className="os-total"><b>Total Investment</b><span>{total ? money(total) : '$ ________'}</span></div>

          </div>
        </div>
      </div>
    </>
  );
}
